(ns practitest.frontend.components.view-editor
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.column-picker :as column-picker]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.view-editor.basic-builder :as bb]
    [practitest.frontend.components.view-editor.events :as events]
    [practitest.frontend.components.view-editor.subs :as view-editor.subs]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.tabs :refer [tab tabs-container]]
    [practitest.frontend.ptql-editor :as ptql-editor]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ---- views ----

(defn- tooltip-icon [text]
  [tooltip/mux-tooltip
   {}
   text
   (fn [props]
     [:span.view-editor-tooltip-icon props
      [icons/mux-info-circle-nofill]])])

;; TODO: replace with non-bootstrap component once we get rid of bootstrap
(defn- view-as-ptql-switch [form]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])
        can-basic? (and (nil? ptql-error) (bb/fits? (:view-ast form)))
        ptql? (or (= :advanced render-mode) (not can-basic?))]
    [:div.form-switch.view-editor-render-mode-switch
     [:input.form-check-input
      {:type "checkbox"
       :id "view-editor-as-ptql"
       :checked ptql?
       :disabled (not can-basic?)
       :on-change (fn [e]
                    (re-frame/dispatch [::events/set-filter-render-mode
                                        (if (dom/event-value e) :advanced :basic)]))}]
     [:label.form-check-label {:for "view-editor-as-ptql"}
      (get-translation "view-editor.render-mode.as-ptql" "Edit as PTQL")]
     (when-not can-basic?
       [tooltip-icon
        (get-translation "view-editor.render-mode.basic-unavailable.tooltip"
                         "Basic mode isn't available for this filter")])]))

(defn- ptql-section [{:keys [label label-extra value on-change on-parse-result
                             parse-error read-only? auto-focus? source-entity]}]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        users @(re-frame/subscribe [::subs/users])
        groups @(re-frame/subscribe [::subs/groups])
        tags @(re-frame/subscribe [::subs/tags])]
    [:div.view-editor-section
     {:class (if read-only? "is-read-only" "is-editable")}
     (when (or label label-extra)
       [:div.view-editor-section-header
        (when label [:label.view-editor-section-label label])
        (when label-extra label-extra)])
     [ptql-editor/editor
      {:value value
       :on-change on-change
       :on-parse-result on-parse-result
       :fields-by-entity fields-by-entity
       :source-entity source-entity
       :users users
       :groups groups
       :tags (mapv first tags)
       :read-only? read-only?
       :auto-focus? auto-focus?}]
     (when-not read-only?
       [:div.view-editor-ptql-hint
        [:kbd "Ctrl"] " + " [:kbd "Space"]
        " for suggestions"])
     (when parse-error
       [:div.view-editor-ptql-parse-error parse-error])]))

(defn- auto-filter-row [idx value options removable?]
  [:div.auto-filter-row
   [:span.auto-filter-row-indent
    (when (pos? idx) [icons/mux-chevron-right])]
   [:select.form-control
    {:value (or value "")
     :on-change #(re-frame/dispatch [::events/set-auto-filter-field idx (dom/event-value %)])}
    [:option {:value ""} (get-translation "view-editor.auto-filter.none" "(select a field)")]
    (for [{:keys [value label]} options]
      ^{:key value} [:option {:value value} label])]
   [:span.auto-filter-row-action
    (when removable?
      [buttons/icon-button
       {:icon [icons/mux-x]
        :tooltip (get-translation "view-editor.auto-filter.remove" "Remove")
        :on-click #(re-frame/dispatch [::events/remove-auto-filter-level idx])}])]])

(defn- auto-filter-section [form]
  (let [options @(re-frame/subscribe [::view-editor.subs/auto-filter-options])
        max-levels @(re-frame/subscribe [::view-editor.subs/auto-filter-max-levels])
        levels (or (:auto-filter form) [])
        levels (if (empty? levels) [nil] levels)
        last-filled? (not (str/blank? (last levels)))
        can-add? (and last-filled? (< (count levels) max-levels))]
    [:div.view-editor-section.is-editable
     [:div.auto-filter-hide-empty
      [:label
       [:input {:type "checkbox"
                :checked (boolean (:hide-empty-auto-filters form))
                :on-change #(re-frame/dispatch [::events/set-hide-empty-auto-filters (dom/event-value %)])}]
       " " (get-translation "view-editor.auto-filter.hide-empty" "Hide empty values")
       [:span {:on-click dom/prevent-default}
        [tooltip-icon
         (get-translation "view-editor.auto-filter.hide-empty.tooltip"
                          "Don't show auto-filter values that have no matching entities.")]]]]
     [:label.view-editor-section-label
      (get-translation "view-editor.auto-filter.section-label" "Group by")]
     (doall
       (map-indexed
         (fn [idx v]
           ^{:key idx} [auto-filter-row idx v options (and (pos? idx) (= idx (dec (count levels))))])
         levels))
     (when can-add?
       [buttons/button
        {:tertiary? true
         :icon [icons/mux-plus-circle]
         :text (get-translation "view-editor.auto-filter.add-child" "Add level")
         :on-click #(re-frame/dispatch [::events/add-auto-filter-level])}])]))

(defn- name-row [form mode]
  [:div.view-editor-name-row
   [:label.view-editor-name-label {:for "view-editor-name"}
    (get-translation "view-editor.name" "Name")]
   [:div.view-editor-name-row-inputs
    [:input.form-control
     {:id "view-editor-name"
      :type "text"
      :auto-focus (= mode :create)
      :value (:name form)
      :on-change #(re-frame/dispatch [::events/name-changed (dom/event-value %)])}]
    [:label.view-editor-public-toggle
     [:input
      {:type "checkbox"
       :checked (boolean (:public? form))
       :on-change #(re-frame/dispatch [::events/public-changed (dom/event-value %)])}]
     " "
     (get-translation "view-editor.is-public" "Public")
     [:span {:on-click dom/prevent-default}
      [tooltip-icon
       (get-translation "view-editor.is-public.tooltip"
                        "Public views are visible to everyone with access to the project. Otherwise only you can see it.")]]]]])

(defn- filter-type-tabs [form]
  (let [limit-reached? @(re-frame/subscribe [::view-editor.subs/auto-filter-limit-reached?])
        af-mode? (= :auto-filter (:mode form))
        af-toggle-disabled? (and limit-reached? (not af-mode?))]
    [:<>
     [:div.view-editor-filter-type
      [tabs-container {:active-tab-id (or (:mode form) :ptql)
                       :on-toggle #(re-frame/dispatch [::events/set-mode %])}
       [tab {:tab-id :ptql}
        (get-translation "view-editor.filter-type.manual" "Manual")
        [:span {:on-click dom/stop-propagation}
         [tooltip-icon
          (get-translation "view-editor.filter-type.manual.tooltip"
                           "Define filter criteria manually using PTQL.")]]]
       [tab {:tab-id :auto-filter}
        (get-translation "view-editor.filter-type.auto-filter" "Auto Filter")
        [:span {:on-click dom/stop-propagation}
         [tooltip-icon
          (get-translation "view-editor.filter-type.auto-filter.tooltip"
                           "Automatically generate categories for each value of a list field.")]]]]]
     (when af-toggle-disabled?
       [:div.view-editor-filter-type-warning
        (get-translation "view-editor.auto-filter.limit-reached"
                         "Auto-filter limit reached for this entity type")])]))

(defn- ancestors-breadcrumbs-separator []
  [:span.view-editor-ancestors-breadcrumbs-icon [icons/mux-chevron-right]])

(defn- ancestors-breadcrumbs [path]
  (when (seq path)
    [:div.view-editor-ancestors-breadcrumbs
     [:span.view-editor-ancestors-breadcrumbs-icon [icons/mux-folders]]
     (doall
       (->> (for [{:keys [name]} path]
              [:span.view-editor-ancestors-breadcrumbs-item name])
            (interpose [ancestors-breadcrumbs-separator])
            (map-indexed (fn [idx el]
                           (with-meta el {:key idx})))))
     [ancestors-breadcrumbs-separator]]))

(defn- ancestors-section [form source-entity]
  (when-not (str/blank? (:ancestors-ptql-optimized form))
    [:details.view-editor-ancestors-details
     [:summary (get-translation "view-editor.ancestors.toggle" "Show inherited filters")]
     [ptql-section
      {:label nil
       :value (:ancestors-ptql-optimized form)
       :read-only? true
       :source-entity source-entity}]]))

(defn- combined-preview [form source-entity]
  (let [af-mode? (= :auto-filter (:mode form))
        recomputing? @(re-frame/subscribe [::view-editor.subs/recomputing?])
        value (if af-mode?
                (:ancestors-ptql-optimized form)
                (:combined-ptql-optimized form))]
    [ptql-section
     {:label (if (and (not af-mode?) recomputing?)
               (get-translation "view-editor.combined-computing" "Effective filter (computing...)")
               (get-translation "view-editor.combined" "Effective filter (read-only)"))
      :value (or value "")
      :read-only? true
      :source-entity source-entity}]))

(defn- manual-filter-section [form mode source-entity]
  (let [render-mode @(re-frame/subscribe [::view-editor.subs/filter-render-mode])
        ptql-error @(re-frame/subscribe [::view-editor.subs/ptql-error])]
    (case render-mode
      :basic [:div.view-editor-section.is-editable
              [:div.view-editor-section-header
               [:label.view-editor-section-label
                (get-translation "view-editor.view-ptql" "This view's filter")]
               [view-as-ptql-switch form]]
              [bb/basic-filter-section form]]
      :advanced [ptql-section
                 {:label (get-translation "view-editor.view-ptql" "This view's filter")
                  :label-extra [view-as-ptql-switch form]
                  :value (:view-ptql form)
                  :read-only? false
                  :auto-focus? (= mode :edit)
                  :on-change #(re-frame/dispatch [::events/view-ptql-changed %])
                  :on-parse-result #(re-frame/dispatch [::events/set-ptql-error %])
                  :parse-error ptql-error
                  :source-entity source-entity}])))

(defn- fields-section [form]
  (let [available-fields @(re-frame/subscribe [::view-editor.subs/available-fields])]
    [:details.view-editor-fields-details
     [:summary (get-translation "view-editor.default-fields" "Default fields")]
     [column-picker/column-picker
      {:empty-hint (get-translation "view-editor.default-fields.empty-hint"
                                    "No columns selected - the project's default columns for this entity type will be used.")
       :available-fields available-fields
       :selected (or (:definition form) [])
       :on-change #(re-frame/dispatch [::events/set-definition %])}]]))

(defn- view-editor-body []
  (let [form @(re-frame/subscribe [::view-editor.subs/form])
        loading? @(re-frame/subscribe [::view-editor.subs/loading?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])
        af-mode? (= :auto-filter (:mode form))]
    (if loading?
      [:div.view-editor-loading (get-translation "view-editor.loading" "Loading...")]
      [:div.view-editor-body
       [name-row form mode]
       [filter-type-tabs form]
       [ancestors-section form source-entity]
       (if af-mode?
         [auto-filter-section form]
         [manual-filter-section form mode source-entity])
       [combined-preview form source-entity]
       [fields-section form]])))

(defn view-editor-modal []
  (let [open? @(re-frame/subscribe [::view-editor.subs/open?])
        mode @(re-frame/subscribe [::view-editor.subs/mode])
        saving? @(re-frame/subscribe [::view-editor.subs/saving?])
        error @(re-frame/subscribe [::view-editor.subs/error])
        form @(re-frame/subscribe [::view-editor.subs/form])]
    [modal/bootstrap-modal
     {:is-open? open?
      :on-request-close #(re-frame/dispatch [::events/close])
      :close-button? true
      :centered? true
      :scrollable? true
      :class "view-editor-modal"}
     [:div.view-editor-modal-title-area
      (when-let [path (seq (:ancestors-path form))]
        [ancestors-breadcrumbs path])
      [:h2 (if (= mode :create)
             (get-translation "view-editor.create-title" "Create view")
             (get-translation "view-editor.edit-title" "Edit view"))]]
     [:<>
      (when error [:div.view-editor-error error])
      [view-editor-body]]
     [:div.view-editor-footer
      [buttons/button {:text (get-translation "view-editor.cancel" "Cancel")
                       :on-click #(re-frame/dispatch [::events/close])}]
      (let [disable-reason @(re-frame/subscribe [::view-editor.subs/save-disabled-reason])
            tooltip (when disable-reason
                      (case disable-reason
                        :empty-name (get-translation "view-editor.save-disabled.empty-name"
                                                     "Name cannot be empty")
                        :incomplete (get-translation "view-editor.save-disabled.incomplete"
                                                     "Complete or remove unfinished clauses")
                        :invalid-ptql (get-translation "view-editor.save-disabled.invalid-ptql"
                                                       "Fix the filter syntax error before saving")
                        :no-auto-filter (get-translation "view-editor.save-disabled.no-auto-filter"
                                                         "Select at least one auto-filter field")))]
        [buttons/button {:primary? true
                         :text (if saving?
                                 (get-translation "view-editor.saving" "Saving...")
                                 (get-translation "view-editor.save" "Save"))
                         :tooltip tooltip
                         :disabled? (or saving? (some? disable-reason))
                         :on-click #(re-frame/dispatch [::events/save])}])]]))

(comment
  (re-frame/dispatch [::events/open-edit 21625])

  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 4"
                                                   :view-ptql "Priority = \"High\""}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "LINKED TO TestSet"}}])
  (re-frame/dispatch [::events/open-create {:parent-id 82051
                                            :entity-type "Issue"
                                            :seed {:name "Test seed name 5"
                                                   :view-ptql "COUNT(LINKED TO TestSet WHERE ('Run Status' = \"BLOCKED\")) > 0"}}]))
